import React, { useMemo, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import ReactTooltip from 'react-tooltip'
import Input from 'uiKit/inputs/Input'
import Select from 'uiKit/Select/Select'
import DatePicker from 'react-datepicker'
import { FormContainer, Heading, FormGroup, FormLabel, FlexContainer, ErrorText } from './SetupForm.styles'
import styled from 'styled-components'
import 'react-datepicker/dist/react-datepicker.css'
import { setFormField, setCarerOptions, clearFormError, setFormErrors } from '../../../reducers/supervisionForm'
import { EditIcon } from 'uiKit/icons/EditIcon'
import { getUsersList } from '../../../api/supervision'
import { components } from 'react-select'

const DateIcon = () => {
  return (
    <div className="react-datepicker__calendar-icon">
      <EditIcon />
    </div>
  )
}

// Custom input component to handle the edit icon click
interface CustomInputProps {
  value?: string
  onClick?: () => void
  placeholder?: string
  className?: string
  disabled?: boolean
}

const CustomInput = React.forwardRef<HTMLInputElement, CustomInputProps>(
  ({ value, onClick, placeholder, className, disabled }, ref) => (
    <div
      className="custom-input-container"
      style={{ position: 'relative', opacity: disabled ? 0.7 : 1, cursor: disabled ? 'default' : 'pointer' }}>
      <input value={value || ''} disabled={disabled} onClick={onClick} placeholder={placeholder} className={className} readOnly ref={ref} />
      <div
        className="react-datepicker__calendar-icon"
        onClick={onClick}
        style={{ cursor: disabled ? 'default' : 'pointer' }}>
        <EditIcon />
      </div>
    </div>
  ),
)

const DatePickerWrapper = styled.div`
  .react-datepicker-wrapper {
    width: 100%;
  }
  .react-datepicker__input-container {
    position: relative;
    display: block;
  }
  .react-datepicker__input-container input {
    width: 100%;
    height: 40px;
    border-radius: 4px;
    border: 1px solid var(--color-ghost-grey);
    padding: 0 40px 0 12px; /* Increased right padding to make room for the icon */
    font-size: 14px;
    color: var(--color-text-primary);
    &:focus {
      outline: none;
      border-color: var(--color-primary);
    }
    &::placeholder {
      color: var(--color-text-placeholder);
    }
  }
  .react-datepicker__calendar-icon {
    position: absolute;
    right: 8px;
    top: -1px;
    cursor: pointer;
  }
`

const frequencyOptions = [
  { label: 'One time', value: 'ONE_TIME' },
  { label: 'Monthly', value: 'MONTHLY' },
  { label: 'Quarterly', value: 'QUARTERLY' },
  { label: '6 months', value: 'HALF_YEARLY' },
  { label: 'Annually', value: 'ANNUAL' },
  { label: 'Alternate months', value: 'ALTERNATE_MONTHS' },
]
interface SetupFormProps {
  onFormChange?: (isValid: boolean) => void
  isDisabled?: boolean
}

// Helper function to format date with UTC noon time
const formatDateWithUTCNoon = (date: Date): string => {
  if (!date) return null
  const year = date.getFullYear()
  const month = date.getMonth()
  const day = date.getDate()
  const utcDate = new Date(Date.UTC(year, month, day, 12, 0, 0))
  return utcDate.toISOString()
}

// Custom Menu component with Select All option
const Menu = props => {
  const { selectProps } = props
  const { onSelectAll } = selectProps

  return (
    <components.Menu {...props}>
      {props.options.length > 0 && (
        <div
          style={{
            padding: '8px 12px',
            borderBottom: '1px solid var(--color-ghost-grey)',
            cursor: 'pointer',
            fontWeight: 500,
            color: 'var(--color-text-secondary)',
          }}
          onClick={onSelectAll}>
          Select All
        </div>
      )}
      {props.children}
    </components.Menu>
  )
}

const SetupForm: React.FC<SetupFormProps> = ({ isDisabled = false }) => {
  const dispatch = useDispatch()
  const { form, errors, carerOptions } = useSelector((state: any) => state.supervisionForm)
  const activeBot = useSelector((state: any) => state.activeBot)

  // Helpers
  const clearIfTruthy = (field: string, value: any) => {
    if (value == null) return
    if (value === '') return
    dispatch(clearFormError(field))
  }

  const handleNameChange = (val: any) => {
    if (typeof val !== 'string') return
    // Do not trim on each keystroke so users can type spaces between words.
    // Clear error if, after trimming, there is any content.
    if (val.trim()) {
      clearIfTruthy('name', val)
    }
    dispatch(setFormField('name', val))
  }

  const handleNameBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const raw = e.target.value ?? ''
    const trimmed = raw.trim()
    // Persist the trimmed value on blur
    dispatch(setFormField('name', trimmed))
    if (trimmed === '') {
      dispatch(setFormErrors({ ...errors, name: 'Name cannot be empty' }))
    }
  }

  const handleFrequencyChange = (val: any) => {
    clearIfTruthy('frequency', val)
    dispatch(setFormField('frequency', val))
    if (val?.value !== 'ONE_TIME') {
      dispatch(clearFormError('startDate'))
      dispatch(clearFormError('dueDate'))
    }
  }

  const handleCarersChange = (val: any) => {
    if (Array.isArray(val) && val.length > 0) dispatch(clearFormError('carers'))
    dispatch(setFormField('carers', val))
  }

  const handleStartDateChange = (val: any) => {
    clearIfTruthy('startDate', val)
    dispatch(setFormField('startDate', val))
  }

  const handleDueDateChange = (val: any) => {
    clearIfTruthy('dueDate', val)
    dispatch(setFormField('dueDate', val))
  }

  const handlers: Record<string, (v: any) => void> = {
    name: handleNameChange,
    frequency: handleFrequencyChange,
    carers: handleCarersChange,
    startDate: handleStartDateChange,
    dueDate: handleDueDateChange,
  }

  const onChangeHandler = (field: string, value: any) => {
    const handler = handlers[field]
    if (handler) return handler(value)
    // Default fallback
    clearIfTruthy(field, value)
    dispatch(setFormField(field, value))
  }

  // Handle select all users
  const handleSelectAll = () => {
    if (carerOptions && carerOptions.length > 0) {
      onChangeHandler('carers', carerOptions)
    }
  }

  return (
    <FormContainer>
      <Heading>Setup</Heading>
      <FormGroup>
        <FormLabel>
          Name <span>*</span>
        </FormLabel>
        <Input
          name="name"
          placeholder="Enter name"
          value={form.name}
          onChange={e => onChangeHandler('name', e.target.value)}
          onBlur={handleNameBlur}
          error={errors.name}
          errorMessage={errors.name}
          maxLength={64}
          disabled={isDisabled}
        />
      </FormGroup>
      <FormGroup>
        <FormLabel>
          Frequency <span>*</span>
        </FormLabel>
        <Select
          name="frequency"
          placeholder="Select frequency"
          options={frequencyOptions}
          isDisabled={isDisabled}
          value={form.frequency}
          onChange={value => onChangeHandler('frequency', value)}
          error={errors.frequency}
          errorMessage={errors.frequency}
        />
      </FormGroup>
      {form.frequency?.value === 'ONE_TIME' && (
        <FlexContainer>
          <FormGroup style={{ width: '50%', marginRight: '20px' }}>
            <FormLabel>
              Available from <span>*</span>
              <ReactTooltip
                className="modal-tooltip"
                place="top"
                type="dark"
                effect="solid"
                id="AvailableАromTooltip-1"
                show={true}>
                Care Professional will be able to access their supervision from this date.
              </ReactTooltip>
              <img
                style={{ cursor: 'pointer', marginLeft: '10px' }}
                data-tip
                data-for="AvailableАromTooltip-1"
                src="/images/platform/information-button.svg"
                alt="info icon"
              />
            </FormLabel>
            <DatePickerWrapper>
              <DatePicker
                selected={form.startDate ? new Date(form.startDate) : null}
                onChange={date => {
                  if (!date) return onChangeHandler('startDate', null)
                  onChangeHandler('startDate', formatDateWithUTCNoon(date))
                }}
                dateFormat="dd/MM/yyyy"
                placeholderText="Select available from date"
                className={errors.startDate ? 'error' : ''}
                minDate={new Date()}
                maxDate={form.dueDate ? new Date(form.dueDate) : undefined}
                customInput={<CustomInput disabled={isDisabled} />}
                disabled={isDisabled}
              />
            </DatePickerWrapper>
            {errors.startDate && <ErrorText>{errors.startDate}</ErrorText>}
          </FormGroup>
          <FormGroup style={{ width: '50%' }}>
            <FormLabel>
              Deadline <span>*</span>
            </FormLabel>
            <DatePickerWrapper>
              <DatePicker
                selected={form.dueDate ? new Date(form.dueDate) : null}
                onChange={date => {
                  if (!date) return onChangeHandler('dueDate', null)
                  onChangeHandler('dueDate', formatDateWithUTCNoon(date))
                }}
                dateFormat="dd/MM/yyyy"
                placeholderText="Select deadline date"
                disabled={isDisabled}
                className={errors.dueDate ? 'error' : ''}
                minDate={form.startDate ? new Date(form.startDate) : new Date()}
                customInput={<CustomInput disabled={isDisabled} />}
              />
            </DatePickerWrapper>
            {errors.dueDate && <ErrorText>{errors.dueDate}</ErrorText>}
          </FormGroup>
        </FlexContainer>
      )}
      <FormGroup>
        <FormLabel>
          Assign users <span>*</span>
        </FormLabel>
        <Select
          name="carers"
          placeholder="Select users"
          options={carerOptions || []}
          value={form.carers}
          onChange={value => onChangeHandler('carers', value)}
          error={errors.carers}
          errorMessage={errors.carers}
          isMulti
          components={{ Menu }}
          onSelectAll={handleSelectAll}
          onMenuOpen={() => {
            if (activeBot?.id) {
              getUsersList(activeBot.id)
                .then(response => {
                  dispatch(setCarerOptions([...response.carers_list, ...response.managers_list]))
                })
                .catch(error => {
                  console.error('Failed to fetch users list:', error)
                })
            }
          }}
        />
      </FormGroup>
    </FormContainer>
  )
}

export default SetupForm
